// ScoreBreakdownCard — a compact "weight of each criterion" visualization
// that lives on the call-detail page. Turns the abstract weights ("This
// criterion is worth 8pt, that one 3pt") into a bar chart with clear
// green/red segments so managers see WHERE the score came from at a
// glance, not just what it is.
//
// Data source: the same `criteria_results[]` that the checklist uses.
// - not_applicable_for_type entries are excluded entirely.
// - not_needed_for_win entries (from Won calls) are shown as amber
//   "credited @ 50%" so the maths reads correctly.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Scale, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScoreBreakdownCard = ({ grading }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [deepLink, setDeepLink] = useState(null);

  // Resolve the "How is this scored?" destination on mount. The backend
  // decides: for kb_generic tenants → the exact KB module used to grade
  // this call; for tfvf / none → the /help scoring reference. We fetch
  // ONCE per call (grading.call_id is stable for the lifetime of this
  // card render) and avoid re-triggering on every rows recompute.
  const callId = grading?.call_id;
  useEffect(() => {
    if (!callId || !token) return;
    axios
      .get(`${API}/training/link-for-call/${callId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setDeepLink(r.data))
      .catch(() => setDeepLink({ url: '/help', program: 'none' }));
  }, [callId, token]);

  const rows = useMemo(() => {
    const crits = (grading?.criteria_results || []).filter(c => !c.not_applicable_for_type);
    const total = crits.reduce((s, c) => s + (Number(c.weight) || 1), 0) || 1;
    return crits
      .map((c) => {
        const w = Number(c.weight) || 1;
        const tone = c.not_needed_for_win
          ? 'amber'
          : c.met ? 'emerald' : 'rose';
        return {
          key: c.description || `crit-${Math.random()}`,
          description: c.description,
          weight: w,
          pctOfTotal: Math.round((w / total) * 100),
          met: c.met,
          notNeeded: !!c.not_needed_for_win,
          tone,
        };
      })
      .sort((a, b) => b.weight - a.weight);
  }, [grading]);

  if (!grading || rows.length === 0) return null;

  const totalPossible = rows.reduce((s, r) => s + r.weight, 0);
  const earned = rows.reduce((s, r) => {
    if (r.met) return s + r.weight;
    if (r.notNeeded) return s + 0.5 * r.weight;
    return s;
  }, 0);

  return (
    <Card className="border-slate-200" data-testid="score-breakdown-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="w-4 h-4 text-slate-500" /> Score breakdown
          <span className="text-xs text-slate-400 font-normal ml-1">
            · {earned.toFixed(1)} / {totalPossible} weight earned
          </span>
          {deepLink?.url && (
            <button
              type="button"
              onClick={() => navigate(deepLink.url)}
              className="ml-auto text-xs font-semibold text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 no-underline"
              data-testid="score-breakdown-how-scored-link"
              title={
                deepLink.program === 'kb_generic'
                  ? 'Open the exact KB used to grade this call'
                  : 'Open the platform-wide scoring reference'
              }
            >
              {deepLink.program === 'kb_generic' ? 'Open KB module' : 'How is this scored?'}
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r) => {
          const barTone =
            r.tone === 'emerald' ? 'bg-emerald-500'
              : r.tone === 'amber' ? 'bg-amber-400'
                : 'bg-rose-500';
          const labelTone =
            r.tone === 'emerald' ? 'text-emerald-800'
              : r.tone === 'amber' ? 'text-amber-800'
                : 'text-rose-800';
          const badge =
            r.notNeeded ? 'Not needed (won)'
              : r.met ? 'Met'
                : 'Missed';
          return (
            <div key={r.key} className="text-xs">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-slate-700 truncate flex-1 pr-2" title={r.description}>{r.description}</span>
                <span className="text-slate-500 shrink-0 tabular-nums">
                  <span className="font-mono">{r.weight}pt</span>
                  <span className="text-slate-400"> · {r.pctOfTotal}%</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${barTone} transition-all`}
                    style={{ width: `${r.pctOfTotal}%` }}
                  />
                </div>
                <span className={`text-[10px] font-semibold ${labelTone} w-24 text-right`}>
                  {badge}
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ScoreBreakdownCard;
